import React, {useLayoutEffect, useMemo, useRef} from "react";
import ReactDOM from "react-dom";
import {useSelector} from "react-redux";
import Mark from "mark.js";
import {Roles} from "@evennt-tech/api-v2-core";
import {CheckVerifiedDomainStatus} from "@evennt-tech/api-v2-external";
import Alert from "view/components/Alert";
import useRedirectToExternalLink from "view/components/RichText/hooks/useRedirectToExternalLink";
import {useRichTextImages} from "view/components/RichText/hooks/useRichTextImages";
import {useFilterUnverifiedIframes} from "view/components/RichText/hooks/useFilterUnverifiedIframes";
import {useReadonlyIframeStatus} from "view/components/RichText/hooks/useReadonlyIframeStatus";
import {useEmbedFocusScrollGuard} from "view/components/RichText/hooks/useEmbedFocusScrollGuard";
import {
  stripPersistedExhibitorIframeNotices,
  stripPersistedIframeStatusNotices,
} from "view/components/RichText/tools/iframeStatusNotices";
import {sanitizeHtml} from "view/components/SafeHtml";
import {selectUser} from "store/auth/selectors";
import {useGlossarySearchQuery} from "store/glossary/glossary.store.selectors";
import clsx from "clsx";
import useStyles from "./styles";

const GlossaryTermDescription = ({
  description,
  className,
}: {
  description: string;
  className?: string;
}) => {
  const classes = useStyles();

  const contentRef = useRef<HTMLDivElement | null>(null);
  const searchQuery = useGlossarySearchQuery();
  const {role} = useSelector(selectUser);
  const renderedDescription = useMemo(() => {
    if (!description) return description;

    const withNoticesStripped = (() => {
      if (role === Roles.EXHIBITOR) {
        return stripPersistedExhibitorIframeNotices(description);
      }

      if (role === Roles.ORGANISER) {
        return stripPersistedIframeStatusNotices(description);
      }

      return description;
    })();

    // mXSS defence-in-depth — see SafeHtml's comment.
    return sanitizeHtml(withNoticesStripped);
  }, [description, role]);

  useRedirectToExternalLink(contentRef, renderedDescription);
  useRichTextImages();
  useFilterUnverifiedIframes(contentRef, renderedDescription);
  const overlayEntries = useReadonlyIframeStatus(
    contentRef,
    renderedDescription
  );
  useEmbedFocusScrollGuard(contentRef, renderedDescription);

  useLayoutEffect(() => {
    if (!contentRef.current) return;

    // Strip pointer-events:none that was baked into older saved iframes by
    // a previous version of the editor's hover-toolbar code, so they stay
    // interactive on the read-only Show Info view.
    contentRef.current
      .querySelectorAll<HTMLIFrameElement>("iframe")
      .forEach((node) => {
        if (node.style.pointerEvents === "none") {
          node.style.removeProperty("pointer-events");
        }
      });
    // Strip the editor-only iframe hover toolbar that may have been
    // serialized into older saved HTML — read-only views must never show it.
    contentRef.current
      .querySelectorAll(".ffair-iframe-toolbar")
      .forEach((node) => node.remove());

    const instance = new Mark(contentRef.current);
    instance.unmark({
      exclude: [".ffair-iframe-wrap", ".ffair-iframe-wrap *"],
      done() {
        instance.mark(searchQuery, {
          acrossElements: true,
          separateWordSearch: false,
          caseSensitive: false,
          element: "strong",
          exclude: [".ffair-iframe-wrap", ".ffair-iframe-wrap *"],
        });
      },
    });

    return () => {
      instance.unmark();
    };
  }, [renderedDescription, searchQuery, classes]);

  return (
    <>
      <div
        className={clsx(classes.root, className)}
        ref={contentRef}
        dangerouslySetInnerHTML={{__html: renderedDescription}}
      />
      {overlayEntries.map((entry, index) =>
        ReactDOM.createPortal(
          <div
            key={`${entry.status}-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "40px 16px",
              pointerEvents: "none",
              backgroundColor: "#ffffff",
            }}
          >
            <Alert
              severity={
                entry.status === CheckVerifiedDomainStatus.REJECTED
                  ? "error"
                  : "warning"
              }
              fullWidth
            >
              {entry.status === CheckVerifiedDomainStatus.REJECTED
                ? "Sorry, the link was rejected. The IFrame is not visible to Exhibitors. Please contact support for more details."
                : "Waiting for Approval. Domain sent to FFAIR Team for verification. The IFrame is not visible to Exhibitors. Please contact support for more details."}
            </Alert>
          </div>,
          entry.wrap
        )
      )}
    </>
  );
};

export default React.memo(GlossaryTermDescription);
